/**
 * What the Big Job Agent runs on, what else it can reach, and what it is told.
 *
 * One form and one Save, because the API replaces the MCP server list whole:
 * removing a row here is a deletion that has not happened yet, and saying so by
 * leaving the row out of the request is the only encoding the endpoint has.
 */
import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/errors'
import type { AgentModel, AgentSettings, GatewayConnection } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NativeSelect } from '@/shared/ui/native-select'
import { Separator } from '@/shared/ui/separator'
import { Skeleton } from '@/shared/ui/skeleton'
import { Switch } from '@/shared/ui/switch'
import { Textarea } from '@/shared/ui/textarea'
import {
  AGENT_INSTRUCTIONS_MAX,
  agentBody,
  connectionKey,
  draftFrom,
  instructionsTooLong,
  newServer,
  parseConnectionKey,
  serverErrorsFrom,
  type AgentDraft,
  type McpServerDraft,
} from '../agent'
import {
  useAgentConnections,
  useAgentModels,
  useAgentSettings,
  useUpdateAgent,
} from '../agent-queries'

export function AgentSection({ workspaceId }: { workspaceId: number }) {
  const { data: settings } = useAgentSettings(workspaceId)

  if (settings === undefined) return <Skeleton className="h-64" />

  return <AgentForm workspaceId={workspaceId} settings={settings} />
}

function AgentForm({
  workspaceId,
  settings,
}: {
  workspaceId: number
  settings: AgentSettings
}) {
  const { t } = useTranslation()
  // Only asked for when there is a gateway to ask. Without one the list is
  // empty by construction, and the request would be a round trip to be told so.
  const connections = useAgentConnections(workspaceId, settings.gateway_configured)
  const save = useUpdateAgent(workspaceId)

  const [draft, setDraft] = useState<AgentDraft>(() => draftFrom(settings))
  const [saved, setSaved] = useState(false)
  const [serverErrors, setServerErrors] = useState<Record<number, string>>({})
  const [refused, setRefused] = useState<string | null>(null)

  const models = useAgentModels(
    workspaceId,
    settings.gateway_configured ? draft.service : '',
    draft.alias,
  )

  const tooLong = instructionsTooLong(draft.instructions)

  function change(values: Partial<AgentDraft>): void {
    setSaved(false)
    setDraft((current) => ({ ...current, ...values }))
  }

  function changeServer(key: string, values: Partial<McpServerDraft>): void {
    change({
      servers: draft.servers.map((server) =>
        server.key === key ? { ...server, ...values } : server,
      ),
    })
  }

  return (
    <div className="space-y-4">
      <h3 className="text-base font-medium">{t('bigJobAgent.title')}</h3>
      <p className="text-sm text-muted-foreground">{t('bigJobAgent.description')}</p>

      {!settings.gateway_configured && (
        <Alert>
          <AlertTitle>{t('bigJobAgent.notConfiguredTitle')}</AlertTitle>
          <AlertDescription>{t('bigJobAgent.notConfiguredDescription')}</AlertDescription>
        </Alert>
      )}

      <Separator />

      <section className="space-y-3">
        <h4 className="font-medium">{t('bigJobAgent.modelTitle')}</h4>
        <p className="text-sm text-muted-foreground">
          {t('bigJobAgent.modelDescription')}
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="agent-connection">{t('bigJobAgent.connection')}</Label>
          <NativeSelect
            id="agent-connection"
            disabled={!settings.gateway_configured}
            value={connectionKey(draft.service, draft.alias)}
            onChange={(event) => {
              const next = parseConnectionKey(event.target.value)
              // A model id belongs to one provider, so it does not survive a switch.
              change(next.service === draft.service ? next : { ...next, model: '' })
            }}
          >
            <option value=":">
              {settings.gateway_configured
                ? t('bigJobAgent.connectionPlaceholder')
                : t('bigJobAgent.connectionUnavailable')}
            </option>
            {connectionOptions(draft, connections.data ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </NativeSelect>
          <p className="text-sm text-muted-foreground">
            {t('bigJobAgent.connectionHelper')}
          </p>
          {/*
            Reported here rather than as a toast: the gateway is one of four
            things this panel edits, and the other three are unaffected.
          */}
          {connections.isError && (
            <p className="text-sm text-muted-foreground">
              {t('bigJobAgent.connectionsFailed')}
            </p>
          )}
          {settings.gateway_configured &&
            !connections.isError &&
            (connections.data ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t('bigJobAgent.noConnections')}{' '}
                {t('bigJobAgent.supportedServices', {
                  services: settings.supported_services.join(', '),
                })}
              </p>
            )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="agent-model">{t('bigJobAgent.model')}</Label>
          {/*
            Typed only when the provider could not be asked, so a gateway that
            is down does not stop the model being set.
          */}
          {models.isError ? (
            <Input
              id="agent-model"
              disabled={!settings.gateway_configured}
              value={draft.model}
              placeholder={t('bigJobAgent.modelPlaceholder')}
              onChange={(event) => change({ model: event.target.value })}
            />
          ) : (
            <NativeSelect
              id="agent-model"
              disabled={
                !settings.gateway_configured || draft.service === '' || models.isPending
              }
              value={draft.model}
              onChange={(event) => change({ model: event.target.value })}
            >
              <option value="">
                {draft.service === ''
                  ? t('bigJobAgent.modelChooseConnection')
                  : models.isPending
                    ? t('bigJobAgent.modelLoading')
                    : t('bigJobAgent.modelChoose')}
              </option>
              {modelOptions(draft.model, models.data ?? []).map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name === model.id ? model.id : `${model.name} (${model.id})`}
                </option>
              ))}
            </NativeSelect>
          )}
          <p className="text-sm text-muted-foreground">
            {models.isError
              ? t('bigJobAgent.modelsFailed')
              : t('bigJobAgent.modelHelper')}
          </p>
        </div>
      </section>

      <Separator />

      <section className="space-y-3">
        <h4 className="font-medium">{t('bigJobAgent.mcpTitle')}</h4>
        <p className="text-sm text-muted-foreground">{t('bigJobAgent.mcpDescription')}</p>

        {draft.servers.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('bigJobAgent.noServers')}</p>
        )}

        <ul aria-label={t('bigJobAgent.mcpTitle')} className="space-y-4">
          {draft.servers.map((server, index) => (
            <ServerRow
              key={server.key}
              server={server}
              error={serverErrors[index]}
              onChange={(values) => {
                changeServer(server.key, values)
              }}
              onRemove={() => {
                change({
                  servers: draft.servers.filter((one) => one.key !== server.key),
                })
              }}
            />
          ))}
        </ul>

        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            change({ servers: [...draft.servers, newServer()] })
          }}
        >
          <Plus />
          {t('bigJobAgent.addServer')}
        </Button>
      </section>

      <Separator />

      <section className="space-y-3">
        <h4 className="font-medium">{t('bigJobAgent.instructionsTitle')}</h4>
        <p className="text-sm text-muted-foreground">
          {t('bigJobAgent.instructionsDescription')}
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="agent-instructions">{t('bigJobAgent.instructions')}</Label>
          <Textarea
            id="agent-instructions"
            rows={6}
            aria-invalid={tooLong}
            value={draft.instructions}
            placeholder={t('bigJobAgent.instructionsPlaceholder')}
            onChange={(event) => change({ instructions: event.target.value })}
          />
          <p className="text-sm text-muted-foreground">
            {t('bigJobAgent.instructionsCount', {
              count: draft.instructions.length,
              max: AGENT_INSTRUCTIONS_MAX,
            })}
          </p>
          {tooLong && (
            <p className="text-sm text-destructive">
              {t('bigJobAgent.instructionsTooLong', { max: AGENT_INSTRUCTIONS_MAX })}
            </p>
          )}
        </div>
      </section>

      {refused !== null && <p className="text-sm text-destructive">{refused}</p>}

      <div className="flex items-center justify-end gap-3">
        {saved && !save.isPending && (
          <p className="text-sm text-muted-foreground">{t('bigJobAgent.saved')}</p>
        )}
        <Button
          disabled={save.isPending || tooLong}
          onClick={() => {
            setServerErrors({})
            setRefused(null)
            save.mutate(agentBody(draft), {
              onSuccess: (answer) => {
                setSaved(true)
                // Reseeded from the answer, which is what carries the ids the
                // server assigned to the rows that had none. Without it the
                // next save would create them again and delete these.
                setDraft(draftFrom(answer))
              },
              onError: (error) => {
                if (!(error instanceof ApiError)) {
                  setRefused(t('bigJobAgent.saveFailed'))
                  return
                }
                const rows = serverErrorsFrom(error.detail)
                setServerErrors(rows)
                // Only when nothing was pinned to a row, so a refusal is never
                // reported in two places at once.
                if (Object.keys(rows).length === 0) {
                  setRefused(error.userMessage().description)
                }
              },
            })
          }}
        >
          {t('bigJobAgent.save')}
        </Button>
      </div>
    </div>
  )
}

/**
 * The connections to offer, with the stored one kept even when it is not among
 * them.
 *
 * A gateway that is down, or a connection somebody removed there, would
 * otherwise leave the select showing the placeholder -- and saving from that
 * state would silently unset a choice nobody meant to change.
 */
function connectionOptions(
  draft: AgentDraft,
  connections: readonly GatewayConnection[],
): { value: string; label: string }[] {
  const options = connections.map((connection) => ({
    value: connectionKey(connection.service, connection.alias),
    label:
      connection.account_label === ''
        ? connection.display_name
        : `${connection.display_name} — ${connection.account_label}`,
  }))

  const stored = connectionKey(draft.service, draft.alias)
  if (draft.service === '' || options.some((option) => option.value === stored)) {
    return options
  }
  return [{ value: stored, label: stored }, ...options]
}

/** The provider's models, plus the stored one when the provider no longer lists it. */
function modelOptions(stored: string, models: readonly AgentModel[]): AgentModel[] {
  if (stored === '' || models.some((model) => model.id === stored)) return [...models]
  return [{ id: stored, name: stored }, ...models]
}

function ServerRow({
  server,
  error,
  onChange,
  onRemove,
}: {
  server: McpServerDraft
  error: string | undefined
  onChange: (values: Partial<McpServerDraft>) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const id = server.key

  return (
    <li className="space-y-3 rounded-md border p-3">
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-name`}>{t('bigJobAgent.serverName')}</Label>
        <Input
          id={`${id}-name`}
          value={server.name}
          aria-invalid={error !== undefined}
          placeholder={t('bigJobAgent.serverNamePlaceholder')}
          onChange={(event) => {
            onChange({ name: event.target.value })
          }}
        />
        <p className="text-sm text-muted-foreground">
          {t('bigJobAgent.serverNameHelper')}
        </p>
        {error !== undefined && <p className="text-sm text-destructive">{error}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${id}-url`}>{t('bigJobAgent.serverUrl')}</Label>
        <Input
          id={`${id}-url`}
          value={server.url}
          placeholder="https://example.com/mcp"
          onChange={(event) => {
            onChange({ url: event.target.value })
          }}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${id}-token`}>{t('bigJobAgent.serverToken')}</Label>
        <Input
          id={`${id}-token`}
          type="password"
          value={server.token}
          disabled={server.clearToken}
          placeholder={
            server.hasToken ? '••••••••' : t('bigJobAgent.serverTokenPlaceholder')
          }
          onChange={(event) => {
            onChange({ token: event.target.value })
          }}
        />
        <p className="text-sm text-muted-foreground">
          {server.clearToken
            ? t('bigJobAgent.serverTokenCleared')
            : server.hasToken
              ? t('bigJobAgent.serverTokenStored')
              : t('bigJobAgent.serverTokenHelper')}
        </p>
        {/*
          A stored token can be replaced by typing over it, and there is no
          other way to say "remove it" -- an empty box already means "leave it
          alone". So removing one is its own control rather than a state of the
          box.
        */}
        {server.hasToken && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange({ clearToken: !server.clearToken, token: '' })
            }}
          >
            {server.clearToken ? t('bigJobAgent.keepToken') : t('bigJobAgent.clearToken')}
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Switch
            id={`${id}-enabled`}
            checked={server.enabled}
            onCheckedChange={(on) => {
              onChange({ enabled: on })
            }}
          />
          <Label htmlFor={`${id}-enabled`}>{t('bigJobAgent.serverEnabled')}</Label>
        </div>
        <Button variant="ghost" size="sm" onClick={onRemove}>
          <Trash2 />
          {t('bigJobAgent.removeServer')}
        </Button>
      </div>
    </li>
  )
}
